import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { storableError } from '../../util/errors';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';
import { publishedListingResultIds } from '../../util/landingPageListings';
import { createImageVariantConfig } from '../../util/sdkLoader';
import {
  getCityLandingConfig,
  getBoundsForSdk,
  cityTypeSlugToGroup,
} from '../../util/cityLandingPageConfig';
import { isUnifiedSearchEnabled } from '../../util/unifiedSearch';

// Load all listings of a city in one query (Sharetribe SDK max = 100).
// A single page covers realistic city catalogues, ensures every category
// pill / section shows when at least one matching space exists, and gives
// accurate "(N)" counts in CityCategorySections.
const RESULT_PAGE_SIZE = 100;

// With the unified search a chip click is a real navigation (history push) to the same city with
// another space type. The page holds the whole city in one query and filters in memory, so the
// same city is not fetched again while its result is this fresh — the chip then filters instantly,
// without a loading state (spec §4.3). Older results are refetched: a tab left open for an hour
// should not keep showing what was bookable then.
export const CITY_RESULT_MAX_AGE_MS = 15 * 60 * 1000;

const listingTypesParams = config =>
  config.listing.enforceValidListingType
    ? { pub_listingType: config.listing.listingTypes.map(l => l.listingType) }
    : {};

const buildSdkParams = (config, boundsStr) => {
  const bounds = getBoundsForSdk(boundsStr);
  if (!bounds) {
    return null;
  }

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return {
    bounds,
    page: 1,
    perPage: RESULT_PAGE_SIZE,
    include: ['author', 'images'],
    minStock: 1,
    stockMode: 'match-undefined',
    ...listingTypesParams(config),
    'fields.listing': [
      'title',
      'geolocation',
      'price',
      'deleted',
      'state',
      'publicData.listingType',
      'publicData.categoryLevel1',
      'publicData.categoryLevel2',
      'publicData.transactionProcessAlias',
      'publicData.unitType',
      'publicData.cardStyle',
      'publicData.pickupEnabled',
      'publicData.shippingEnabled',
      'publicData.priceVariationsEnabled',
      'publicData.priceVariants',
    ],
    // Beide Extended-Data-Felder des Nutzers MUESSEN hier stehen, in jedem
    // Sparse-Fieldset, das Nutzer laedt — nicht nur dort, wo etwas davon sichtbar
    // ist: combinedResourceObjects (util/data.js) ersetzt attributes.profile beim
    // Zusammenfuehren KOMPLETT, und sanitizeUser erzwingt publicData:{} als
    // eigenen Schluessel (Destructuring-Default). Eine sparsame Antwort loescht
    // also die Extended Data einer bereits geladenen vollen Antwort aus dem Store.
    // publicData traegt stripeConnected — die Weiche zwischen Standard-Checkout
    // und Soft-Booking (util/softReservation.js) —, metadata traegt stripeVerified
    // fuer das Badge. Fehlt publicData, landet ein zahlungsbereiter Anbieter im
    // langsameren Soft-Booking.
    'fields.user': [
      'profile.displayName',
      'profile.abbreviatedName',
      'profile.publicData',
      'profile.metadata',
    ],
    'fields.image': [
      'variants.scaled-small',
      'variants.scaled-medium',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    'limit.images': 1,
  };
};

export const fetchCityListings = createAsyncThunk(
  'CityLandingPage/fetchCityListings',
  async ({ citySlug, config }, thunkAPI) => {
    const { dispatch, rejectWithValue, extra: sdk } = thunkAPI;
    const city = getCityLandingConfig(citySlug);
    if (!city) {
      return rejectWithValue(storableError(new Error('Unknown city')));
    }
    const params = buildSdkParams(config, city.boundsStr);
    if (!params) {
      return rejectWithValue(storableError(new Error('Invalid bounds')));
    }

    try {
      const response = await sdk.listings.query(params);
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };
      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      return {
        citySlug,
        ids: publishedListingResultIds(response.data),
      };
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

const cityLandingPageSlice = createSlice({
  name: 'CityLandingPage',
  initialState: {
    currentCitySlug: null,
    listingIds: [],
    fetchInProgress: false,
    fetchError: null,
    // The city whose listings `listingIds` holds, and when they arrived (ms epoch).
    loadedCitySlug: null,
    loadedAt: null,
  },
  reducers: {
    clearCityLanding: () => ({
      currentCitySlug: null,
      listingIds: [],
      fetchInProgress: false,
      fetchError: null,
      loadedCitySlug: null,
      loadedAt: null,
    }),
    // Leaving the city page ends the "same city, no refetch" window: other pages load the same
    // listing entities with fewer fields (the homepage rows carry no categoryLevel1), so after
    // Back/Forward the city's ids would point at entities that lost their space type — and the
    // fallback to "Alle" then rewrote /coworking/zurich/meetingraum (seen on dev 2026-10-18).
    markCityListingsStale: state => {
      state.loadedAt = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchCityListings.pending, (state, action) => {
        state.currentCitySlug = action.meta.arg.citySlug;
        state.fetchInProgress = true;
        state.fetchError = null;
      })
      .addCase(fetchCityListings.fulfilled, (state, action) => {
        state.listingIds = action.payload.ids;
        state.fetchInProgress = false;
        state.loadedCitySlug = action.payload.citySlug;
        state.loadedAt = Date.now();
      })
      .addCase(fetchCityListings.rejected, (state, action) => {
        state.fetchInProgress = false;
        state.fetchError = action.payload;
        state.listingIds = [];
        state.loadedCitySlug = null;
        state.loadedAt = null;
      });
  },
});

export const { clearCityLanding, markCityListingsStale } = cityLandingPageSlice.actions;

export default cityLandingPageSlice.reducer;

/**
 * Whether the store already holds (or is fetching) this city's listings — then a navigation within
 * the same city (a space-type chip, Back/Forward between two types) needs no new query.
 *
 * @param {Object} cityState state.CityLandingPage
 * @param {string} citySlug
 * @param {number} [now] ms epoch
 * @returns {boolean}
 */
export const hasFreshCityListings = (cityState, citySlug, now = Date.now()) => {
  if (!cityState || cityState.currentCitySlug !== citySlug || cityState.fetchError) {
    return false;
  }
  if (cityState.fetchInProgress) {
    return true;
  }
  return (
    cityState.loadedCitySlug === citySlug &&
    typeof cityState.loadedAt === 'number' &&
    now - cityState.loadedAt < CITY_RESULT_MAX_AGE_MS
  );
};

export const loadData = (params, search, config) => async (dispatch, getState, sdk) => {
  const { citySlug, typSlug } = params || {};
  const city = getCityLandingConfig(citySlug);
  // The city×type route (/coworking/:citySlug/:typSlug) shares this loader. A type word the page
  // does not know renders a 404, so there is nothing to fetch for it.
  if (!city || (typSlug != null && !cityTypeSlugToGroup(typSlug))) {
    return Promise.resolve();
  }

  const state = getState();
  const currentUser = state.user?.currentUser;
  const isAuthorized = currentUser && isUserAuthorized(currentUser);
  const hasViewingRights = currentUser && hasPermissionToViewData(currentUser);
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const canFetchListings =
    !isPrivateMarketplace || (isPrivateMarketplace && isAuthorized && hasViewingRights);

  if (!canFetchListings) {
    return Promise.resolve();
  }

  if (isUnifiedSearchEnabled() && hasFreshCityListings(state.CityLandingPage, citySlug)) {
    return Promise.resolve();
  }

  await dispatch(fetchCityListings({ citySlug, config }));
};
